<template>
  <b-card v-if="totalLabsCount > 0" class="mb-3" no-body>
    <b-card-header class="py-2 bg-light d-flex justify-content-between align-items-center">
      <span class="d-flex align-items-center">
        <i class="fas fa-flask mr-2"></i>งานแลป
        <small v-if="!showAllLabs && hiddenCount > 0" class="text-muted ml-2">
          ({{ labs.length }}/{{ totalLabsCount }})
        </small>
      </span>
      <span class="d-flex align-items-center flex-wrap" style="gap: 4px;">
        <span
          v-for="g in statusGroups"
          :key="g.value || 'unknown'"
          class="lab-chip"
        >
          <span class="lab-chip-dot" :style="{ backgroundColor: g.color }"></span>
          <span class="lab-chip-count">{{ g.count }}</span>
          <span class="lab-chip-label">{{ g.label }}</span>
        </span>
      </span>
    </b-card-header>
    <b-card-body class="p-0">
      <b-table
        v-if="labs.length > 0"
        :items="labs"
        :fields="fields"
        :tbody-tr-class="rowClass"
        table-class="table-condensed"
        small
        hover
        responsive
        head-variant="light"
        thead-class="d-none"
        class="mb-0"
      >
        <template #cell(name)="{ item }">
          {{ item.mLab?.nameTh || item.mLab?.nameEn || '-' }}
        </template>
        <template #cell(toothNo)="{ item }">
          {{ item.toothNo || '-' }}
        </template>
        <template #cell(status)="{ item }">
          <div v-if="item.isUpdating" class="text-center">
            <b-spinner small></b-spinner>
          </div>
          <b-dropdown
            v-else
            size="xs"
            :text="getStatusLabel(item.status)"
            :variant="getStatusVariant(item.status)"
            boundary="window"
          >
            <b-dropdown-item
              v-for="s in statusList"
              :key="s.value"
              @click="onStatusChange(item, s.value)"
            >{{ s.label }}</b-dropdown-item>
          </b-dropdown>
        </template>
        <template #cell(creationDt)="{ item }">
          {{ formatDate(item.creationDt) || '-' }}
        </template>
      </b-table>
      <div v-else class="text-center text-muted py-2 small">
        ไม่มี lab ในช่วง ±30 วัน ของวันนัดนี้
      </div>
      <div v-if="showToggleButton" class="text-right pr-2 pb-1">
        <b-button
          size="sm"
          class="lab-toggle-btn py-0 px-2"
          @click="$emit('toggle-show-all')"
        >
          <i :class="showAllLabs ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="mr-1"></i>
          <span v-if="showAllLabs">ย่อ</span>
          <span v-else>+{{ hiddenCount }}</span>
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { mapActions } from "vuex";
import { LAB_STATUS_LIST, getLabStatusByValue } from "@/constants/labStatus";
import { formatDate } from "@/utils/utils";

export default {
  name: "LabLinkCard",
  props: {
    labs: { type: Array, default: () => [] },
    totalLabsCount: { type: Number, default: 0 },
    showAllLabs: { type: Boolean, default: false }
  },
  data() {
    return {
      statusList: LAB_STATUS_LIST,
      fields: [
        { key: "name", label: "รายการแลป", class: "narrow-spacing" },
        { key: "toothNo", label: "ซี่ฟัน", class: "narrow-spacing d-none d-md-table-cell" },
        { key: "status", label: "สถานะ", class: "narrow-spacing" },
        { key: "creationDt", label: "วันที่สร้าง", class: "narrow-spacing d-none d-lg-table-cell" }
      ]
    };
  },
  computed: {
    hiddenCount() {
      return Math.max(0, this.totalLabsCount - this.labs.length);
    },
    showToggleButton() {
      return this.showAllLabs || this.hiddenCount > 0;
    },
    statusGroups() {
      const groups = LAB_STATUS_LIST
        .map(s => ({
          value: s.value,
          label: s.label,
          variant: s.variant,
          color: s.color,
          count: this.labs.filter(l => l.status === s.value).length
        }))
        .filter(g => g.count > 0);

      const unknownCount = this.labs.filter(l => !l.status).length;
      if (unknownCount > 0) {
        const fallback = getLabStatusByValue(null);
        groups.push({
          value: null,
          label: fallback.label,
          variant: fallback.variant,
          color: fallback.color,
          count: unknownCount
        });
      }
      return groups;
    }
  },
  methods: {
    ...mapActions({
      updateLabStatus: "moduleLab/updateLabStatus"
    }),
    formatDate,
    getStatusLabel(status) {
      return getLabStatusByValue(status).label;
    },
    getStatusVariant(status) {
      return getLabStatusByValue(status).variant;
    },
    rowClass(item) {
      return item.status === 'Delivered' ? 'delivered-lab' : '';
    },
    async onStatusChange(lab, newStatus) {
      this.$set(lab, "isUpdating", true);
      try {
        await this.updateLabStatus({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          labId: lab.id,
          status: newStatus
        });
        lab.status = newStatus;
        this.$emit("statusUpdated", lab);
      } catch (err) {
        console.error("Failed to update lab status:", err);
      } finally {
        this.$set(lab, "isUpdating", false);
      }
    }
  }
};
</script>

<style>
.table-condensed {
  font-size: 0.8rem;
}
.table .delivered-lab {
  background-color: #f8f9fa !important;
}
.table .delivered-lab td {
  color: #6c757d !important;
}

.lab-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border: 1px solid #dee2e6;
  border-radius: 999px;
  background: #fff;
  font-size: 0.72rem;
  line-height: 1.4;
  color: #495057;
  white-space: nowrap;
}
.lab-chip-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.lab-chip-count {
  font-weight: 600;
}
.lab-chip-label {
  color: #6c757d;
}

.lab-toggle-btn {
  font-size: 0.7rem;
  color: #adb5bd;
  border: 1px solid #e9ecef;
  background: transparent;
  box-shadow: none;
}
.lab-toggle-btn:hover,
.lab-toggle-btn:focus,
.lab-toggle-btn:active {
  color: #495057 !important;
  border-color: #ced4da !important;
  background: #f8f9fa !important;
  box-shadow: none !important;
}

.btn-purple {
  background-color: #6f42c1;
  border-color: #6f42c1;
  color: #fff;
}
.btn-purple:hover,
.btn-purple:focus {
  background-color: #5a32a3;
  border-color: #5a32a3;
  color: #fff;
}
</style>
